import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import type { AppCreateCustomerRequest, AppUpdateCustomerRequest, CustomerDto } from '../api/generated';

interface CustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  customer?: CustomerDto | null;
  onSave: (data: AppCreateCustomerRequest | AppUpdateCustomerRequest, customerId?: string) => Promise<void>;
}

const CustomerModal: React.FC<CustomerModalProps> = ({
  isOpen,
  onClose,
  mode,
  customer,
  onSave,
}) => {
  const [formData, setFormData] = useState<AppCreateCustomerRequest | AppUpdateCustomerRequest>(
    mode === 'create'
      ? {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          gdprConsent: true,
        }
      : {
          firstName: customer?.firstName || '',
          lastName: customer?.lastName || '',
          email: customer?.email || '',
          phone: customer?.phone || '',
          isActive: customer?.isActive ?? true,
        }
  );

  const [isSaving, setIsSaving] = useState(false);

  // Reset form when customer changes
  useEffect(() => {
    if (mode === 'edit' && customer) {
      setFormData({
        firstName: customer.firstName || '',
        lastName: customer.lastName || '',
        email: customer.email || '',
        phone: customer.phone || '',
        isActive: customer.isActive ?? true,
      });
    } else if (mode === 'create') {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        gdprConsent: true,
      });
    }
  }, [mode, customer, isOpen]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(formData, mode === 'edit' ? customer?.id : undefined);
      onClose();
    } catch (error) {
      console.error('Chyba při ukládání zákazníka:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const isFormValid = () => {
    return formData.firstName && formData.lastName && formData.email && formData.phone;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md">
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              {mode === 'create' ? 'Nový zákazník' : 'Upravit zákazníka'}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Jméno *</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={e => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Jan"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Příjmení *</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={e => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Novák"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="jan.novak@email.cz"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Telefon *</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={e => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="+420 123 456 789"
              />
            </div>

            {mode === 'edit' && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={(formData as AppUpdateCustomerRequest).isActive}
                  onChange={e => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="mr-2"
                />
                <label htmlFor="isActive" className="text-sm text-gray-700">
                  Aktivní zákazník
                </label>
              </div>
            )}

            {mode === 'create' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="gdprConsent"
                    checked={(formData as AppCreateCustomerRequest).gdprConsent}
                    onChange={e => setFormData(prev => ({ ...prev, gdprConsent: e.target.checked }))}
                    className="mr-2"
                  />
                  <label htmlFor="gdprConsent" className="text-xs text-gray-700">
                    Souhlas se zpracováním osobních údajů (GDPR) *
                  </label>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t">
            <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-800">
              Zrušit
            </button>
            <button
              onClick={handleSave}
              disabled={!isFormValid() || isSaving}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Ukládání...' : mode === 'create' ? 'Vytvořit zákazníka' : 'Uložit změny'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerModal;

